/**
 * D332: whether the game is running installed, and the steps to install it when it is not.
 * "Remind me to install" and when the banner was last dismissed are stored per device, the
 * same way as the other settings in `apps/web/src/idle/three/pref.ts` (a key in
 * localStorage, read once and kept in a module-level value, missing or unreadable storage
 * keeps the default). That file is map-art's, so the same pattern lives here instead.
 */
import { useEffect, useState } from 'preact/hooks';
import { getIdleContent } from '@hedgerow/data';
import { isIos } from './notifications.ts';

export interface InstallEnv {
  userAgent: string;
  maxTouchPoints: number;
  /** Opened from the home screen or as a standalone window, on any platform. */
  standalone: boolean;
}

export type InstallKind = 'ios' | 'android' | 'desktop' | 'other';

/** Whether the game is already running installed. */
export function isInstalled(env: InstallEnv): boolean {
  return env.standalone;
}

/** Which steps fit this device, from the user agent. */
export function installKind(env: InstallEnv): InstallKind {
  if (isIos(env.userAgent, env.maxTouchPoints)) return 'ios';
  if (/Android/.test(env.userAgent)) return 'android';
  if (/Chrome\/|Chromium\/|Edg\//.test(env.userAgent)) return 'desktop';
  return 'other';
}

/** The words for each kind, from STYLE.md: plain, and they say what to do. Android and desktop
 * are overtaken by an "Install" button where the browser offers `beforeinstallprompt`. */
export const INSTALL_STEPS: Record<InstallKind, string> = {
  ios: 'Tap Share, then Add to Home Screen.',
  android: 'Open the menu, then tap Install app.',
  desktop: 'Tap the install icon in the address bar.',
  other: 'In your browser menu, tap Add to Home Screen or Install.',
};

export function readInstallEnv(): InstallEnv {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const win = typeof window === 'undefined' ? undefined : window;
  const standalone = !!(
    (win &&
      typeof win.matchMedia === 'function' &&
      win.matchMedia('(display-mode: standalone)').matches) ||
    (nav as (Navigator & { standalone?: boolean }) | undefined)?.standalone
  );
  return {
    userAgent: nav?.userAgent ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    standalone,
  };
}

/** Captured `beforeinstallprompt` (Android Chrome, some desktop Chromium). */
interface DeferredInstallEvent extends Event {
  prompt: () => Promise<unknown>;
}

let deferred: DeferredInstallEvent | null = null;
const deferredListeners = new Set<(has: boolean) => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as unknown as DeferredInstallEvent;
    for (const fn of deferredListeners) fn(true);
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    for (const fn of deferredListeners) fn(false);
  });
}

export function hasInstallPrompt(): boolean {
  return !!deferred;
}

/** Shows the browser's own install prompt, where one was captured. Does nothing otherwise. */
export async function promptInstall(): Promise<void> {
  const e = deferred;
  if (!e) return;
  deferred = null;
  for (const fn of deferredListeners) fn(false);
  await e.prompt();
}

/** The banner and the settings row: whether a captured prompt can offer an Install button. */
export function useHasInstallPrompt(): boolean {
  const [has, setHas] = useState(hasInstallPrompt());
  useEffect(() => {
    deferredListeners.add(setHas);
    return () => void deferredListeners.delete(setHas);
  }, []);
  return has;
}

const REMIND_KEY = 'hedgerow-install-remind';
/** Exported so the browser specs can pre-dismiss the banner: it is not part of the flow
 * they test and would otherwise sit over what they tap. */
export const INSTALL_SNOOZE_KEY = 'hedgerow-install-snooze-until';
const SNOOZE_KEY = INSTALL_SNOOZE_KEY;
/** How long dismissing the banner hides it for (D332, `idleContent.install.snoozeDays`). */
export const INSTALL_SNOOZE_MS = getIdleContent().install.snoozeDays * 24 * 60 * 60 * 1000;

function readRemind(): boolean {
  try {
    return globalThis.localStorage?.getItem(REMIND_KEY) !== '0';
  } catch {
    return true;
  }
}

let remindOn = readRemind();
const remindListeners = new Set<(on: boolean) => void>();

export function isInstallRemindOn(): boolean {
  return remindOn;
}

export function setInstallRemind(value: boolean): void {
  remindOn = value;
  try {
    globalThis.localStorage?.setItem(REMIND_KEY, value ? '1' : '0');
  } catch {
    // Kept for this session only.
  }
  for (const fn of remindListeners) fn(value);
}

/** The settings row: "Remind me to install", on by default. */
export function useInstallRemind(): [boolean, (on: boolean) => void] {
  const [value, setValue] = useState(remindOn);
  useEffect(() => {
    remindListeners.add(setValue);
    return () => void remindListeners.delete(setValue);
  }, []);
  return [value, setInstallRemind];
}

function readSnooze(): number {
  try {
    return Number(globalThis.localStorage?.getItem(SNOOZE_KEY) ?? '0') || 0;
  } catch {
    return 0;
  }
}

let snoozeUntil = readSnooze();
const snoozeListeners = new Set<(until: number) => void>();

function setSnoozeUntil(value: number): void {
  snoozeUntil = value;
  try {
    globalThis.localStorage?.setItem(SNOOZE_KEY, String(value));
  } catch {
    // Kept for this session only.
  }
  for (const fn of snoozeListeners) fn(value);
}

/** Dismissing the banner: hides it until this many milliseconds have passed. */
export function dismissInstallPrompt(now: number): void {
  setSnoozeUntil(now + INSTALL_SNOOZE_MS);
}

/** The Notifications setting's iPhone link: clears any snooze, so the banner shows again. */
export function showInstallPromptNow(): void {
  setSnoozeUntil(0);
}

/** The banner: whether now is still within an earlier dismissal. */
export function useInstallSnoozedUntil(): number {
  const [value, setValue] = useState(snoozeUntil);
  useEffect(() => {
    snoozeListeners.add(setValue);
    return () => void snoozeListeners.delete(setValue);
  }, []);
  return value;
}
